import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { friendlyApiError } from '@/utilities/apiErrorMessage';

export type CallRow = Record<string, any>;
export type Cursor = { ms: number; sid: string };
type CallPage = { rows?: CallRow[]; nextCursor?: Cursor | null; total?: number | null };

/** Server `call_key` (the summary's counted leg) when present; else trace, else the leg's own id. */
export const callKey = (row: CallRow): string =>
  row.call_key ? String(row.call_key) : row.trace_id ? String(row.trace_id) : `sid:${row.call_sid ?? ''}`;

// A repeated key replaces the earlier row in place, so a call counts once.
const mergeCalls = (prev: CallRow[], fresh: CallRow[]): CallRow[] => {
  const byKey = new Map(prev.map((r) => [callKey(r), r]));
  fresh.forEach((r) => byKey.set(callKey(r), r));
  return [...byKey.values()];
};

export function useCallReportLoader() {
  const [rows, setRows] = useState<CallRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stoppedAt, setStoppedAt] = useState<Cursor | null>(null);
  const seq = useRef(0);
  const urlFor = useRef<(cursor: Cursor | null) => string>(() => '');

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(async (mySeq: number, from: Cursor | null) => {
    let cursor = from;
    setStoppedAt(null);
    if (cursor) setLoadingMore(true);
    else setLoading(true);
    try {
      do {
        const page = ((await getApiCall(urlFor.current(cursor), undefined)) || {}) as CallPage;
        if (mySeq !== seq.current) return;
        const fresh = Array.isArray(page.rows) ? page.rows : [];
        if (!cursor) {
          setTotal(page.total ?? null);
          setRows(mergeCalls([], fresh));
          setLoading(false);
        } else {
          setRows((prev) => mergeCalls(prev, fresh));
        }
        cursor = page.nextCursor || null;
        setLoadingMore(Boolean(cursor));
      } while (cursor && mySeq === seq.current && alive.current);
    } catch (error) {
      if (mySeq !== seq.current) return;
      if (cursor) {
        setStoppedAt(cursor);
        toast.error(friendlyApiError(error) || 'Loading stopped; press Resume loading to continue');
      }
    } finally {
      if (mySeq === seq.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const start = useCallback(
    (url: (cursor: Cursor | null) => string) => {
      seq.current += 1;
      urlFor.current = url;
      setRows([]);
      setTotal(null);
      void run(seq.current, null);
    },
    [run],
  );

  const resume = useCallback(() => {
    // Own seq: a double click drops the first run instead of loading pages twice.
    if (stoppedAt) void run(++seq.current, stoppedAt);
  }, [run, stoppedAt]);

  const cancel = useCallback(() => {
    seq.current += 1;
    setRows([]);
    setTotal(null);
    setStoppedAt(null);
    setLoading(false);
    setLoadingMore(false);
  }, []);

  const loaded = rows.length;
  const pct = total ? Math.min(100, Math.floor((loaded * 100) / total)) : 100;
  const progressText =
    total == null ? `${loaded.toLocaleString()} records` : `${loaded.toLocaleString()} of ${total.toLocaleString()} records (${pct}%)`;

  return { rows, total, loading, loadingMore, stoppedAt, progressText, start, resume, cancel };
}
